import { captureLiveModulesLayout } from '../Three-JS-Viewer/Controllers/Common';
import { HideObjectType } from '../../../../@types/enums';
import { useViewerStore } from '../../../../store/store';
import { captureLiveLayoutCardMetrics } from '../../../../helpers/aiDesignProductionSync';

type Combo = { key: string; roofFaceIds: string[] };

/** Snapshot from `roofInfoArr` after simulating a layout — matches dashboard `roofCards` shape. */
function captureLayoutRoofSummariesFromStore(): Array<{
    roofFaceId: string;
    roofNum: unknown;
    solarAccess: number;
    roofMultiplier: number;
    moduleCount: number;
    pointCount: number;
    avgPtHash: number;
    productionApprox: number;
    productionIsExact: boolean;
}> {
    const state = useViewerStore.getState() as any;
    const roofInfoArr = (state.roofInfoArr || []) as any[];
    const cards = roofInfoArr
        .map((r) => {
            const moduleCount =
                typeof r.modules === 'number' ? r.modules : parseInt(String(r.modules ?? '0'), 10) || 0;
            if (moduleCount <= 0) return null;
            const solarRaw = r.solarAccess;
            const solarNum = typeof solarRaw === 'number' ? solarRaw : parseFloat(String(solarRaw ?? '0')) || 0;
            const prod = parseFloat(String(r.actual_production ?? '0')) || 0;
            return {
                roofFaceId: r.roofFaceId,
                roofNum: r.roofNum,
                solarAccess: solarNum,
                roofMultiplier: typeof r.roofMultiplier === 'number' ? r.roofMultiplier : 0,
                moduleCount,
                pointCount: 0,
                avgPtHash: Math.round(solarNum),
                productionApprox: Math.round(prod),
                productionIsExact: true,
            };
        })
        .filter(Boolean) as Array<{
        roofFaceId: string;
        roofNum: unknown;
        solarAccess: number;
        roofMultiplier: number;
        moduleCount: number;
        pointCount: number;
        avgPtHash: number;
        productionApprox: number;
        productionIsExact: boolean;
    }>;
    cards.sort((a, b) => (b.productionApprox ?? 0) - (a.productionApprox ?? 0));
    return cards;
}

function mergeLayoutRoofSummariesForKey(layoutKey: string): void {
    const cards = captureLayoutRoofSummariesFromStore();
    useViewerStore.setState((prev: any) => ({
        ...prev,
        layoutRoofSummariesByKey: {
            ...(prev.layoutRoofSummariesByKey || {}),
            [layoutKey]: cards,
        },
    }));
}

type SimulateLayoutsArgs = {
    sceneViewer: any;
    keys: string[];
    simulatingLayouts: boolean;
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: { roofFaceIds?: string[] } | null | undefined;
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;
    selectedRoofCombinationKey: string;
    restoreDefaultLayoutFromSnapshot: () => Promise<void>;
    simulateLayoutForRoofIds: (
        roofFaceIds: string[],
        opts?: { dir?: 'port' | 'land' | 'both' | null; minEff?: number | null; layoutCacheKey?: string },
    ) => Promise<{ production: number; modules: number; efficiency: number | null; weightedSolarAverage: number | null }>;
    setSimulatingLayouts: (next: boolean) => void;
    setSimulatingLayoutIndex: (next: number) => void;
    setSimulatingLayoutTotal: (next: number) => void;
    setLayoutMetricsByKey: (next: (prev: Record<string, any>) => Record<string, any>) => void;
    setLayoutProductionByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setLayoutModulesByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
    setLayoutSimulationTimeByKey: (next: (prev: Record<string, number>) => Record<string, number>) => void;
};

export async function runSimulateLayoutsForKeysAction(args: SimulateLayoutsArgs): Promise<void> {
    const {
        sceneViewer,
        keys,
        simulatingLayouts,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        roofCombinationsFromAi,
        selectedRoofCombinationKey,
        restoreDefaultLayoutFromSnapshot,
        simulateLayoutForRoofIds,
        setSimulatingLayouts,
        setSimulatingLayoutIndex,
        setSimulatingLayoutTotal,
        setLayoutMetricsByKey,
        setLayoutProductionByKey,
        setLayoutModulesByKey,
        setLayoutSimulationTimeByKey,
    } = args;

    if (!sceneViewer || !keys.length || simulatingLayouts) return;
    const viewerAllRoofIds: string[] = (sceneViewer.roofMeshArr ?? [])
        .map((r: any) => r.roofFaceId)
        .filter(Boolean);
    const defaultIds =
        defaultMaxfillRoofIds.length > 0
            ? defaultMaxfillRoofIds
            : (bestRoofsForLayout?.roofFaceIds?.length ?? 0) > 0
              ? (bestRoofsForLayout?.roofFaceIds ?? [])
              : viewerAllRoofIds;
    const allCombos: Combo[] = [
        { key: 'default', roofFaceIds: defaultIds },
        ...(roofCombinationsFromAi || []).map((c: any) => ({ key: c.name, roofFaceIds: c.roofFaceIds ?? [] })),
    ].filter((c) => (c.roofFaceIds?.length ?? 0) > 0);
    const combos = allCombos.filter((c) => keys.includes(c.key));
    if (!combos.length) return;

    let cancelled = false;
    setSimulatingLayouts(true);
    setSimulatingLayoutIndex(0);
    setSimulatingLayoutTotal(combos.length);
    try {
        for (let i = 0; i < combos.length; i++) {
            if (cancelled) break;
            const combo = combos[i];
            const layoutStartMs = Date.now();
            setSimulatingLayoutIndex(i + 1);
            setSimulatingLayoutTotal(combos.length);
            if (i > 0) await new Promise((r) => setTimeout(r, 400));
            if (cancelled) break;
            if (combo.key === 'default') {
                // Viewer may still be on another combo from a prior iteration — restore true default max-fill first.
                await restoreDefaultLayoutFromSnapshot();
            } else {
                await simulateLayoutForRoofIds(combo.roofFaceIds, {
                    layoutCacheKey: combo.key,
                });
            }
            const live = captureLiveLayoutCardMetrics();
            const production = live.production;
            const modules = live.modules;
            const efficiency = live.efficiency;
            const solarAvg = live.solarAccess;
            if (cancelled) break;
            try {
                const stateNow = useViewerStore.getState() as any;
                const treeGroup = sceneViewer?.treeGroup as any;
                const treeChildren = treeGroup?.children as any[] | undefined;
                const treeCountForLayout = Array.isArray(treeChildren) ? treeChildren.length : null;
                const roofInfoArr = (stateNow.roofInfoArr as any[]) || [];
                const roofsWithModules = roofInfoArr.filter((r: any) => Number(r.modules ?? 0) > 0);
                const roofsWithModulesCount = roofsWithModules.length;
                const roofCountForLayout =
                    combo.key === 'default'
                        ? roofsWithModulesCount > 0
                            ? roofsWithModulesCount
                            : defaultIds.length > 0
                              ? defaultIds.length
                              : null
                        : roofsWithModulesCount || null;
                const multiplier = live.multiplier;
                setLayoutMetricsByKey((prev) => ({
                    ...prev,
                    [combo.key]: {
                        efficiency,
                        solarAccess: solarAvg,
                        treeCount: treeCountForLayout,
                        roofCount: roofCountForLayout,
                        multiplier,
                    },
                }));
            } catch {
                // ignore
            }
            setLayoutProductionByKey((prev) => ({ ...prev, [combo.key]: production }));
            setLayoutModulesByKey((prev) => ({ ...prev, [combo.key]: modules }));
            setLayoutSimulationTimeByKey((prev) => ({ ...prev, [combo.key]: Date.now() - layoutStartMs }));
            const modulesLayout = captureLiveModulesLayout(sceneViewer);
            if (Array.isArray(modulesLayout) && modulesLayout.length) {
                useViewerStore.setState((prev: any) => ({
                    ...prev,
                    layoutModulesLayoutByKey: {
                        ...(prev.layoutModulesLayoutByKey || {}),
                        [combo.key]: modulesLayout,
                    },
                }));
            }
            mergeLayoutRoofSummariesForKey(combo.key);
            try {
                sceneViewer?.clearTreeLabel?.();
                sceneViewer?.toggleTreeLabel?.();
                const img = sceneViewer?.getSquareImageOfSite?.();
                if (img) {
                    useViewerStore.setState((prev: any) => ({
                        ...prev,
                        layoutSnapshotsByKey: { ...(prev.layoutSnapshotsByKey || {}), [combo.key]: img },
                    }));
                }
                sceneViewer?.clearTreeLabel?.();
            } catch {
                // ignore
            }
        }
        const finalKey = selectedRoofCombinationKey || 'default';
        const finalCombo = allCombos.find((c) => c.key === finalKey) ?? allCombos[0];
        if (!cancelled && finalCombo?.roofFaceIds?.length) {
            if (finalKey === 'default') await restoreDefaultLayoutFromSnapshot();
            else await simulateLayoutForRoofIds(finalCombo.roofFaceIds);
        }
    } finally {
        setSimulatingLayouts(false);
        setSimulatingLayoutIndex(0);
        setSimulatingLayoutTotal(0);
        try {
            sceneViewer?.setVisibleGroup?.('maps', true);
            if (sceneViewer?.mapCustom) sceneViewer.mapCustom.visible = true;
            sceneViewer?.setVisibleGroup?.('tree', true);
            sceneViewer?.hideObject?.(HideObjectType.GhostTree, useViewerStore.getState().isHideAllGhostTrees);
        } catch {
            // ignore
        }
    }
}
